import { ReactElement, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { InspectionTranslationKeys, FormTranslationKeys } from 'intl/keys';
import { formatTranslation } from 'intl';
import { useIntl } from 'react-intl';

import { MemoStatefulRichTextField } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { CollapsibleInspectionComment } from 'components/inspection/CollapsibleInspectionComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import {
  GraphicUploader,
  InputFormGraphicUploader,
} from 'components/common/graphicUploader';
import {
  useComplianceCheckAutoOpenCheck,
  useDocumentDeepLinkActive,
} from 'components/common/graphicUploader/documentRouteSync';
import { useProtocolOnViewForChecklistItem } from 'components/inspection/protocolLookup';
import {
  useInspection,
  PROTOCOL_ITEM_UPLOADER_PREFIX,
  useInspectionStore,
  useInspectionFieldDisabled,
  isInspectionFieldDisabled,
} from 'store/inspection';
import {
  createComplianceCheckDocumentsBaseUrl,
  createInspectionChecklistItemRoute,
  createInspectionComplianceCheckRoute,
} from 'views/inspections/single/helpers';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { ATTACHMENTS_UPLOADER_PRESET } from 'components/common/graphicUploader/attachmentsPreset';
import { DocumentToProtocolFromUrlBinder } from 'shared/domain/document/documentBinder';
import { buildProtocolItemCommentFormKey } from 'shared/domain/protocolAnswer/formKeys';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { useDisplayedFilesStoreHasFiles } from 'components/common/gallery/filesStore';
import { RichTextView } from 'components/common/RichTextEditor/RichTextView';
import { fieldLabelSx } from 'shared/styles/typography';
import { isRichTextEmpty } from 'shared/utils/richText/length';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { MemoProtocolItemIssues } from '../ProtocolItemIssues';
import {
  protocolItemSectionSx,
} from '../ProtocolItemIssues/styles';

interface ProtocolItemCommentAndAttachmentsProps {
  checklistItemId: string;
  allExpanded?: boolean;
}

export function ProtocolItemCommentAndAttachments({
  checklistItemId,
  allExpanded,
}: ProtocolItemCommentAndAttachmentsProps): ReactElement {
  const intl = useIntl();
  const { values } = useInputForm();
  const { inspection, inspectionDocuments } = useInspection();
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const atomicSaveEnabled = useInspectionStore.use.atomicSaveEnabled();
  const resolveFieldDisabled = useInspectionFieldDisabled();
  const fieldDisabled = resolveFieldDisabled(false);
  const readOnly = isInspectionFieldDisabled(fieldDisabled);

  const protocolItem = useProtocolOnViewForChecklistItem(checklistItemId);

  const protocolItemIdFromUrl = useAppUrlParams(UrlParamName.PROTOCOL_ITEM_ID);
  const effectiveProtocolItemId = protocolItem?._id ?? protocolItemIdFromUrl;

  const complianceAutoOpenCheck = useComplianceCheckAutoOpenCheck(
    checklistItemId,
    effectiveProtocolItemId
  );

  const getComplianceBaseUrl = useCallback((): string => {
    if (!inspection?._id) {
      return '';
    }

    if (effectiveProtocolItemId) {
      return createInspectionComplianceCheckRoute(
        projectId,
        inspection._id,
        effectiveProtocolItemId
      );
    }

    return createInspectionChecklistItemRoute(
      projectId,
      inspection._id,
      checklistItemId
    );
  }, [
    checklistItemId,
    effectiveProtocolItemId,
    inspection?._id,
    projectId,
  ]);

  const shouldMountUploaderForRoute = useDocumentDeepLinkActive(
    complianceAutoOpenCheck,
    inspection?._id ? getComplianceBaseUrl : undefined
  );

  const commentFormKey = buildProtocolItemCommentFormKey(checklistItemId);
  const commentValue =
    (values[commentFormKey] as string | undefined) ??
    protocolItem?.complianceCheck?.comment ??
    '';
  const hasComment = !isRichTextEmpty(commentValue);

  const commentFieldProps = useMemo(() => {
    return {
      formKey: commentFormKey,
      required: false,
      labelId: InspectionTranslationKeys.comment,
      fieldName: `text-field-${commentFormKey}`,
      disabled: fieldDisabled,
      localStorageKey: 'inspection-checklistItem-comment-html',
      reserveSpaceForHelperText: true,
      dense: true,
      'data-qa': 'inspection_checklistItem_comment_field',
      multiline: true,
      minRows: 3,
      issueParams: null,
      placeholder: formatTranslation(
        intl,
        InspectionTranslationKeys.commentAddPlaceholder
      ),
    };
  }, [commentFormKey, fieldDisabled, intl]);

  const protocolDocuments = protocolItem?._id
    ? inspectionDocuments[protocolItem._id]
    : undefined;

  const complianceCheckUploaderConfig = useMemo(
    () => ({
      ...ATTACHMENTS_UPLOADER_PRESET,
      attachmentDropTarget: 'inspection' as const,
      baseUrl: createComplianceCheckDocumentsBaseUrl(
        inspection?._id || '',
        protocolItem?._id || ''
      ),
      atomicChanges: atomicSaveEnabled && !readOnly,
      previewOnly: readOnly,
      dataQaPrefix: `protocol-item-of-${checklistItemId}-`,
      internalEntityBaseUrl: getComplianceBaseUrl,
      autoOpenCheck: complianceAutoOpenCheck,
      documentBinderFactory: (baseUrl?: string) => {
        if (!baseUrl) throw new Error('From url binder requires URL');
        return new DocumentToProtocolFromUrlBinder(baseUrl);
      },
    }),
    [
      atomicSaveEnabled,
      checklistItemId,
      complianceAutoOpenCheck,
      effectiveProtocolItemId,
      getComplianceBaseUrl,
      inspection?._id,
      protocolItem?._id,
      readOnly,
    ]
  );

  const GU = useGraphicUploader(protocolDocuments, complianceCheckUploaderConfig);
  const hasAttachments = useDisplayedFilesStoreHasFiles(GU.filesStore);
  // Editable rows always offer the add tile (as on issues); read-only rows
  // show the gallery only when there is something to see.
  const showAttachmentsUploader =
    !readOnly || hasAttachments || shouldMountUploaderForRoute;

  const attachmentsLabel = formatTranslation(
    intl,
    FormTranslationKeys.sectionProblemRelatedAttachments
  );

  const commentLabel = formatTranslation(intl, InspectionTranslationKeys.comment);

  const showCommentSection = readOnly ? hasComment : true;

  const commentSection = showCommentSection
    ? readOnly
      ? (
        <Box sx={protocolItemSectionSx}>
          <Typography component='p' sx={fieldLabelSx}>
            {commentLabel}
          </Typography>
          <RichTextView content={commentValue} />
        </Box>
      )
      : (
        <Box sx={protocolItemSectionSx}>
          <Typography component='p' sx={fieldLabelSx}>
            {commentLabel}
          </Typography>
          <CollapsibleInspectionComment
            formKey={commentFormKey}
            fallbackValue={protocolItem?.complianceCheck?.comment ?? ''}
            disabled={fieldDisabled}
            labelVariant={InspectionCommentLabelVariant.section}
          >
            <MemoStatefulRichTextField {...commentFieldProps} />
          </CollapsibleInspectionComment>
        </Box>
      )
    : null;

  const attachmentsSection = showAttachmentsUploader
    ? (
      <Box sx={protocolItemSectionSx}>
        <Typography component='p' sx={fieldLabelSx}>
          {attachmentsLabel}
        </Typography>
        {readOnly ? (
          <GraphicUploader {...GU} />
        ) : (
          <InputFormGraphicUploader
            uploaderKey={`${PROTOCOL_ITEM_UPLOADER_PREFIX}-${checklistItemId}`}
            GU={GU}
          />
        )}
      </Box>
    )
    : null;

  return (
    <MemoProtocolItemIssues
      checklistItemId={checklistItemId}
      allExpanded={allExpanded}
      belowToolbar={commentSection}
      attachmentsSection={attachmentsSection}
      alwaysShowSecondaryColumns={!readOnly}
    />
  );
}
